import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useT, useLang } from '../i18n';
import { L } from '../redesign/tokens';
import { useViewportKind } from '../redesign/useViewport';
import { codeMoney } from '../redesign/caseFormat';
import { useCasesFilter } from '../redesign/casesFilterContext';
import { VoiceModalContext } from './voiceContext';

// Ask, the docked panel (Leo's "Ask (docked panel)"): bottom right on every page, opened from the round mic (or Home's centre mic), it STAYS OPEN while you move between pages. Title, Clear, Collapse,
// the conversation, the large mic, a text field with Send. Typed or spoken, both go to the same place (/api/ask/answer, pure agent companies); a spoken question is answered aloud too, a typed one is
// not. Ask never acts alone: what it can offer is a button that opens a list, a request pop-up to review, or an approval CARD that does nothing until the person clicks Approve (the server checks the
// card again). The conversation lives in this component's memory only: never in browser storage, never in an address. Collapse stops any speech; Clear forgets everything.


const MicSvg = ({ size = 30, color = '#FFFFFF' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>
);
const StopSvg = ({ size = 26 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="#FFFFFF" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>;
const TrashSvg = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={L.text} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></svg>;
const DownSvg = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={L.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>;

const iconBtn = { width: 36, height: 36, borderRadius: '50%', border: `1px solid ${L.border}`, background: '#FFFFFF', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 };
const chip = { font: 'inherit', fontSize: 14, fontWeight: 600, padding: '8px 14px', borderRadius: 18, border: `1px solid ${L.accent}`, background: '#FFFFFF', color: L.accent, cursor: 'pointer', textAlign: 'left' };

export function AskProvider({ children }) {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const vk = useViewportKind();
  const filter = useCasesFilter();
  const phone = vk === 'phone';
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState([]);                 // [{ who: 'you' | 'ask', text, links, proposal, request, card }]
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('idle');           // idle | listening | thinking | speaking
  const [error, setError] = useState(null);
  const turnsRef = useRef([]);
  const recorder = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);
  const audio = useRef(null);
  const audioUrl = useRef(null);
  const token = useRef(0);                                // a newer action cancels an older one still on its way
  const inputRef = useRef(null);
  const logRef = useRef(null);

  const put = useCallback((fn) => { turnsRef.current = fn(turnsRef.current); setTurns(turnsRef.current); }, []);

  const stopSpeech = useCallback(() => {
    token.current += 1;
    if (audio.current) { try { audio.current.pause(); } catch { /* nothing playing */ } audio.current = null; }
    if (audioUrl.current) { URL.revokeObjectURL(audioUrl.current); audioUrl.current = null; }
  }, []);
  const stopRecording = useCallback(() => {
    if (recorder.current && recorder.current.state !== 'inactive') { try { recorder.current.onstop = null; recorder.current.stop(); } catch { /* already stopped */ } }
    recorder.current = null;
    if (stream.current) { stream.current.getTracks().forEach((x) => x.stop()); stream.current = null; }
  }, []);
  const quiet = useCallback(() => { stopSpeech(); stopRecording(); setStatus('idle'); }, [stopSpeech, stopRecording]);

  const speak = useCallback(async (text) => {
    const mine = ++token.current;
    setStatus('speaking');
    try {
      const res = await apiFetch('/api/voice/speak', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }) });
      if (!res.ok || mine !== token.current) { if (mine === token.current) setStatus('idle'); return; }
      const blob = await res.blob();
      if (mine !== token.current) return;
      const url = URL.createObjectURL(blob);
      audioUrl.current = url;
      const a = new Audio(url);
      audio.current = a;
      a.onended = () => { if (mine === token.current) { setStatus('idle'); stopSpeech(); } };
      a.onerror = () => { if (mine === token.current) setStatus('idle'); };
      await a.play();
    } catch { if (mine === token.current) setStatus('idle'); }
  }, [stopSpeech]);

  // one question -> one answer. `spoken`: the question was spoken, so the answer is read aloud as well.
  const ask = useCallback(async (text, { spoken = false, language = null } = {}) => {
    const q = String(text || '').trim();
    if (!q) return;
    stopSpeech();
    setError(null);
    const history = turnsRef.current.filter((x) => x.text).map((x) => ({ role: x.who === 'you' ? 'user' : 'assistant', content: x.text }));
    put((ts) => [...ts, { who: 'you', text: q }]);
    setStatus('thinking');
    try {
      const res = await apiFetch('/api/ask/answer', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: q, history, language }) });
      if (!res.ok) { setError(res.status === 429 ? 'ask.err.tooMany' : res.status === 503 ? 'ask.err.unavailable' : 'ask.err.generic'); setStatus('idle'); return; }
      const out = await res.json();
      const reply = out.answer || t('ask.noAnswer');
      put((ts) => [...ts, { who: 'ask', text: reply, links: out.links || [], proposal: out.proposal || null, request: out.request || null, card: out.proposal ? 'ready' : null }]);
      setStatus('idle');
      if (spoken && out.answer) speak(out.answer);
    } catch { setError('ask.err.generic'); setStatus('idle'); }
  }, [put, speak, stopSpeech, t]);

  const send = useCallback(() => { const q = input; setInput(''); ask(q, { language: lang }); }, [ask, input, lang]);

  const onMic = useCallback(async () => {
    if (status === 'speaking') { stopSpeech(); setStatus('idle'); return; }
    if (status === 'listening') {
      const rec = recorder.current;
      if (rec && rec.state !== 'inactive') rec.stop();       // onstop sends it
      return;
    }
    if (status === 'thinking') return;
    setError(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = s;
      const rec = new MediaRecorder(s);
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunks.current, { type: rec.mimeType || 'audio/webm' });
        stopRecording();
        if (!blob.size) { setStatus('idle'); return; }
        setStatus('thinking');
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'question.webm');
          const res = await apiFetch('/api/voice/transcribe', { method: 'POST', body: fd });
          const json = await res.json();
          if (!res.ok || !json.transcript) { setError('ask.err.transcribe'); setStatus('idle'); return; }
          ask(json.transcript, { spoken: true, language: json.language });
        } catch { setError('ask.err.transcribe'); setStatus('idle'); }
      };
      recorder.current = rec;
      rec.start();
      setStatus('listening');
    } catch { stopRecording(); setError('ask.err.mic'); setStatus('idle'); }
  }, [ask, status, stopRecording, stopSpeech]);

  const collapse = useCallback(() => { quiet(); setOpen(false); }, [quiet]);
  const clear = useCallback(() => { quiet(); put(() => []); setInput(''); setError(null); }, [put, quiet]);
  const openVoice = useCallback(() => { setError(null); setOpen(true); requestAnimationFrame(() => inputRef.current && inputRef.current.focus()); }, []);
  useEffect(() => () => { stopSpeech(); stopRecording(); }, [stopSpeech, stopRecording]);
  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [turns, status, error]);

  // ---- what an answer can offer ----
  const openList = (l) => {
    if (l.kind === 'case') { navigate(`/cases/${l.case_id}`); return; }        // the answer was about one case: open that case (Leo I Q3)
    filter.setSupplierIds(l.supplier && l.supplier.id ? [l.supplier.id] : null);
    navigate(`/cases/${l.tile}`, { state: { tab: l.tab && l.tab !== 'all' ? l.tab : undefined } });
  };
  const openRequest = (r) => navigate(`/cases/${r.case_id}`, { state: { openRequest: r.kind } });
  const setCard = (i, patch) => put((ts) => ts.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const approve = async (i) => {
    const turn = turnsRef.current[i];
    if (!turn || !turn.proposal || turn.card !== 'ready') return;
    setCard(i, { card: 'working' });
    try {
      const res = await apiFetch('/api/ask/execute', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ proposal: turn.proposal.token }) });
      if (res.ok) { setCard(i, { card: 'done' }); return; }
      let word = null;
      try { word = (await res.json()).error; } catch { word = null; }
      setCard(i, { card: 'error', cardError: word === 'permission_denied' ? 'ask.card.err.permission' : word === 'already_used' ? 'ask.card.err.used' : word === 'expired' ? 'ask.card.err.expired' : word === 'already_closed' || word === 'not_approvable' ? 'ask.card.err.closed' : 'ask.card.err.failed' });
    } catch { setCard(i, { card: 'error', cardError: 'ask.card.err.failed' }); }
  };
  const undo = async (i) => {
    const turn = turnsRef.current[i];
    if (!turn || !turn.proposal) return;
    setCard(i, { card: 'working' });
    try {
      const res = await apiFetch('/api/agent-cases/case/reopen', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: turn.proposal.card.case_id }) });
      setCard(i, res.ok ? { card: 'undone' } : { card: 'done', cardError: 'ask.card.err.failed' });
    } catch { setCard(i, { card: 'done', cardError: 'ask.card.err.failed' }); }
  };

  const linkLabel = (l) => {
    if (l.kind === 'case') return l.label || t('ask.tile.case');
    const tab = l.tab && l.tab !== 'all' ? t(`cases.list.tab.${l.tile}.${l.tab}`) : null;
    return [t(`ask.tile.${l.tile}`), tab && tab !== `cases.list.tab.${l.tile}.${l.tab}` ? tab : null, l.supplier ? l.supplier.name : null].filter(Boolean).join(' · ');
  };

  const value = useMemo(() => ({ openVoice, voiceActive: turns.length > 0 }), [openVoice, turns.length]);
  const listening = status === 'listening';
  const idleLine = t(`ask.state.${status}`);
  const shell = phone
    ? { left: 8, right: 8, bottom: 8, height: 'min(82vh, 600px)' }
    : { right: 20, bottom: 20, width: 400, height: 'min(660px, 82vh)' };

  return (
    <VoiceModalContext.Provider value={value}>
      {children}
      {open && (
        <section role="dialog" aria-label={t('ask.title')} data-testid="ask-panel" style={{ position: 'fixed', zIndex: 45, ...shell, background: L.card, border: `1px solid ${L.border}`, borderRadius: 20, boxShadow: '0 12px 40px rgba(46,33,22,0.28)', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: L.sans, color: L.text }}>
          <div data-testid="ask-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '14px 16px', borderBottom: `1px solid ${L.rowLine}` }}>
            <h2 style={{ margin: 0, fontFamily: L.serif, fontSize: 22, fontWeight: 500 }}>{t('ask.title')}</h2>
            <span style={{ display: 'inline-flex', gap: 8 }}>
              <button type="button" onClick={clear} aria-label={t('ask.clear')} title={t('ask.clear')} data-testid="ask-clear" style={iconBtn}><TrashSvg /></button>
              <button type="button" onClick={collapse} aria-label={t('ask.collapse')} title={t('ask.collapse')} data-testid="ask-collapse" style={iconBtn}><DownSvg /></button>
            </span>
          </div>

          <div ref={logRef} data-testid="ask-log" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {turns.map((x, i) => (
              <div key={i} data-testid={`ask-turn-${x.who}`} style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: '92%', padding: '10px 14px', borderRadius: 16, boxSizing: 'border-box', alignSelf: x.who === 'you' ? 'flex-end' : 'flex-start', background: x.who === 'you' ? '#F5E9DD' : '#F7F3EC' }}>
                <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: 1.2, color: L.muted, textTransform: 'uppercase' }}>{x.who === 'you' ? t('ask.you') : t('ask.floxir')}</span>
                <span style={{ fontSize: 16, lineHeight: 1.4, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{x.text}</span>
                {x.links && x.links.map((l, k) => <button key={k} type="button" onClick={() => openList(l)} data-testid="ask-link" style={chip}>{t('ask.open', { what: linkLabel(l) })}</button>)}
                {x.request && <button type="button" onClick={() => openRequest(x.request)} data-testid="ask-request" style={chip}>{t('ask.request', { label: x.request.label || '' })}</button>}
                {x.proposal && (
                  <div data-testid="ask-card" data-state={x.card} style={{ border: `1px solid ${L.border}`, borderRadius: 14, padding: '12px 14px', background: '#FBF8F3', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <strong style={{ fontSize: 15 }}>{t('ask.card.title', { label: x.proposal.card.label || '' })}</strong>
                    <span style={{ fontSize: 14, color: L.muted }}>
                      {[x.proposal.card.supplier, x.proposal.card.amount != null ? codeMoney(x.proposal.card.currency, x.proposal.card.amount) : null].filter(Boolean).join(' · ')}
                      {(x.card === 'ready' || x.card === 'working' || x.card === 'error') && ` ${t('ask.card.body')}`}
                    </span>
                    {(x.card === 'ready' || x.card === 'working' || x.card === 'error') && (
                      <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button type="button" disabled={x.card === 'working'} onClick={() => approve(i)} data-testid="ask-approve" style={{ ...chip, background: L.accent, color: '#FFFFFF', opacity: x.card === 'working' ? 0.6 : 1 }}>{t('ask.card.approve')}</button>
                        <button type="button" disabled={x.card === 'working'} onClick={() => setCard(i, { card: 'dismissed' })} data-testid="ask-cancel" style={{ ...chip, borderColor: L.border, color: L.text }}>{t('ask.card.cancel')}</button>
                      </span>
                    )}
                    {x.card === 'done' && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span data-testid="ask-done" style={{ fontSize: 14, color: L.green, fontWeight: 600 }}>{t('ask.card.done', { label: x.proposal.card.label || '' })}</span>
                        <button type="button" onClick={() => undo(i)} data-testid="ask-undo" style={chip}>{t('ask.card.undo')}</button>
                      </span>
                    )}
                    {x.card === 'undone' && <span data-testid="ask-undone" style={{ fontSize: 14, color: L.muted }}>{t('ask.card.undone')}</span>}
                    {x.card === 'dismissed' && <span style={{ fontSize: 14, color: L.muted }}>{t('ask.card.dismissed')}</span>}
                    {x.cardError && <span role="alert" data-testid="ask-card-error" style={{ fontSize: 14, color: '#9E2A1C' }}>{t(x.cardError)}</span>}
                  </div>
                )}
              </div>
            ))}
            {status === 'thinking' && <span data-testid="ask-thinking" style={{ fontSize: 15, color: L.muted }}>{t('ask.state.thinking')}</span>}
            {error && <span role="alert" data-testid="ask-error" style={{ fontSize: 14, color: '#9E2A1C' }}>{t(error)}</span>}
          </div>

          <div style={{ borderTop: `1px solid ${L.rowLine}`, padding: '12px 16px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <button type="button" onClick={onMic} aria-label={t('ask.mic')} data-testid="ask-mic" style={{ width: 64, height: 64, borderRadius: '50%', background: listening ? '#9E2A1C' : L.accent, border: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, boxShadow: '0 4px 12px rgba(46,33,22,0.25)' }}>
              {listening || status === 'speaking' ? <StopSvg /> : <MicSvg />}
            </button>
            <span data-testid="ask-state" style={{ fontSize: 14, color: L.muted }}>{idleLine}</span>
            <form onSubmit={(e) => { e.preventDefault(); if (input.trim()) send(); }} style={{ display: 'flex', gap: 8, width: '100%' }}>
              <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} aria-label={t('ask.field')} placeholder={t('ask.placeholder')} data-testid="ask-input" maxLength={1000}
                style={{ flex: 1, minWidth: 0, height: 44, borderRadius: 10, border: `1px solid ${L.border}`, padding: '0 12px', font: 'inherit', fontSize: 16, background: '#FFFFFF', color: L.text }} />
              <button type="submit" disabled={!input.trim() || status === 'thinking'} data-testid="ask-send" style={{ height: 44, padding: '0 16px', borderRadius: 10, border: 'none', background: !input.trim() || status === 'thinking' ? L.border : L.accent, color: '#FFFFFF', font: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>↑ {t('ask.send')}</button>
            </form>
            <span style={{ fontSize: 12, color: L.muted }}>{t('ask.languages')}</span>
          </div>
        </section>
      )}
    </VoiceModalContext.Provider>
  );
}


